
<template>
  <div>
    <h1>Earns Methoden</h1>

    <p>
      Maßnahmen können mit der überschlägigen oder der genormten / wissenschaftlichen 
      Methode berechnet werden. Die überschlägigen Methoden benötigen weniger Daten 
      und sind somit schneller zu untersuchen. Die genormten / wissenschaftlichen Methoden 
      bieten auf der anderen Seite eine zuverlässige Berechnung welche belastbar sind.
    </p>

    <p>
      Um eine Maßnahme abspeichern zu können, sind in alle technischen
      Pflichtfelder valide Werte einzutragen. Zusätzlich können optional die
      finanziellen Parameter eingetragen werden.
    </p>

    <h1>Kältemaschinen</h1>

    <v-expansion-panels accordion class="mt-5">

      <!-- ======================================== -->
      <!-- PANEL 1 — SCHRITTE VOR DER BERECHNUNG -->
      <!-- ======================================== -->

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Schritte vor der Berechnung</div>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <ol type="1">
            <li>
              <b>Erfassung der aktuellen Betriebsdaten der Kältemaschine</b>
              <ul>
                <li>
                  Sammeln der relevanten Daten der bestehenden Kältemaschine, 
                  einschließlich Kälteleistung, Stromverbrauch, Kältemitteltyp, 
                  Verdampfungs- und Verflüssigungstemperaturen, sowie der 
                  Betriebsstunden.
                </li>
                <li>
                  Erfassung der Betriebsparameter wie Umgebungstemperatur, 
                  Kühlwassertemperatur und Lastprofil (Tages- und 
                  Jahresschwankungen).
                </li>
              </ul>
            </li>

            <li>
              <b>Überprüfung des Ist-Zustands der Kältemaschine</b>
              <ul>
                <li>
                  Überprüfung der Wartungszustände der Kältemaschine, 
                  einschließlich des Zustands des Verdampfers, Verflüssigers, 
                  Kompressors und der Steuerungssysteme.
                </li>
                <li>
                  Feststellen, ob die Kältemaschine im Teillast- oder 
                  Volllastbetrieb läuft und wie effizient dieser Betrieb ist.
                </li>
              </ul>
            </li>

            <li>
              <b>Ermittlung des tatsächlichen Kältebedarfs</b>
              <ul>
                <li>
                  Analysieren, ob der Kältebedarf den tatsächlichen 
                  Anforderungen entspricht oder ob die Anlage überdimensioniert 
                  ist.
                </li>
                <li>
                  Prüfung der Lastprofile der angeschlossenen Verbraucher, 
                  um festzustellen, ob der Betrieb optimiert werden kann.
                </li>
                <li>
                  Evaluierung der Möglichkeit, Lastspitzen zu reduzieren 
                  oder zu verschieben.
                </li>
              </ul>
            </li>

            <li>
              <b>Überprüfung der Regelungs- und Steuerungssysteme</b>
              <ul>
                <li>
                  Untersuchung, ob die Steuerungs- und Regelungssysteme 
                  der Kältemaschine auf dem neuesten Stand sind und ob 
                  sie optimal arbeiten.
                </li>
                <li>
                  Überprüfung der Einsatzmöglichkeiten für moderne 
                  Regelungsstrategien wie Laststeuerung, freie Kühlung 
                  (Free Cooling), oder adaptive Regelungen.
                </li>
              </ul>
            </li>

            <li>
              <b>Optimierungspotenziale im Kältemittelkreislauf identifizieren</b>
              <ul>
                <li>
                  Prüfung des Kältemittelkreislaufs auf mögliche Verluste 
                  oder Ineffizienzen, wie z.B. durch Leckagen, 
                  unzureichende Isolierung oder ungünstige Temperaturverhältnisse.
                </li>
                <li>
                  Überlegen, ob ein Kältemittelwechsel zu einem 
                  energieeffizienteren Betrieb beitragen kann.
                </li>
              </ul>
            </li>

            <li>
              <b>Überprüfung der mechanischen Komponenten</b>
              <ul>
                <li>
                  Analyse des Zustands der mechanischen Komponenten 
                  wie Verdichter, Pumpen und Lüfter.
                </li>
                <li>
                  Überprüfung der Effizienz von Wärmetauschern und 
                  ob diese gereinigt oder ersetzt werden müssen.
                </li>
              </ul>
            </li>

            <li>
              <b>Ermittlung eines optimierten Betriebspunktes</b>
              <ul>
                <li>
                  Festlegung eines neuen, optimierten Betriebspunktes 
                  basierend auf dem tatsächlichen Kältebedarf und den 
                  analysierten Daten.
                </li>
                <li>
                  Optimierung der Verdampfungs- und Verflüssigungstemperaturen.
                </li>
              </ul>
            </li>

            <li>
              <b>Abschätzung des Einsparpotenzials und der Wirtschaftlichkeit</b>
              <ul>
                <li>
                  Analyse des Energieeinsparpotenzials durch Maßnahmen 
                  wie Verdichtermodulation, Frequenzregelung oder Wärmerückgewinnung.
                </li>
                <li>
                  Durchführung einer Wirtschaftlichkeitsanalyse.
                </li>
              </ul>
            </li>

            <li>
              <b>Überprüfung der baulichen Gegebenheiten und des Einbauorts</b>
              <ul>
                <li>
                  Analyse der baulichen Struktur, Rohrleitungen, Isolierungen,
                  Belüftung usw.
                </li>
                <li>
                  Überprüfung des Aufstellorts auf Möglichkeiten zur Effizienzsteigerung.
                </li>
              </ul>
            </li>

            <li>
              <b>Auswahl der optimalen technischen Lösung</b>
              <ul>
                <li>
                  Auswahl der effizientesten Lösung unter Berücksichtigung aller Faktoren.
                </li>
                <li>
                  Bewertung von Systemkompatibilität, Investitions- und Betriebskosten.
                </li>
              </ul>
            </li>

            <li>
              <b>Kostenkalkulation und Angebotserstellung</b>
              <ul>
                <li>
                  Ermittlung aller Kosten für Demontage, Neubau, Installation und Inbetriebnahme.
                </li>
                <li>
                  Erstellung eines detaillierten Angebots.
                </li>
              </ul>
            </li>

            <li>
              <b>Finale Berechnung der Energieeinsparung und Wirtschaftlichkeit</b>
              <ul>
                <li>
                  Berechnung der Energieeinsparung basierend auf den neuen Betriebspunkten.
                </li>
                <li>
                  Endgültige Wirtschaftlichkeitsbewertung.
                </li>
              </ul>
            </li>
          </ol>
        </v-expansion-panel-text>
      </v-expansion-panel>

      
      <!-- ======================================== -->
      <!-- PANEL 2 — REFERENZ + GEPLANT -->
      <!-- ======================================== -->

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">
            Eingabe der Bestandsanlage (Referenz) und der geplanten Anlage
          </div>
        </v-expansion-panel-title>

        <v-expansion-panel-text>

          <table>
            <caption>Übergreifend</caption>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Typ der Kältemaschine</td>
                <td>Auswahl des Kältemaschinentyp</td>
              </tr>
            </tbody>
          </table>

          <table>
            <caption>Parameter der Kompressionskältemaschine</caption>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Nutzungsart</td>
                <td>Auswahl der Anzahl an unterschiedlichen Nutzungsarten</td>
              </tr>
              <tr>
                <td>Nutzungsprofil</td>
                <td>Auswahl des Nutzungsprofiles im Gebäude</td>
              </tr>
              <tr>
                <td>Anteil der Nutzungsart</td>
                <td>Eingabe des prozentualen Nutzungsartanteils an der Gesamtfläche</td>
              </tr>
              <tr>
                <td>Betriebsart</td>
                <td>Auswahl der Vorlauftemperatur</td>
              </tr>
              <tr>
                <td>Feuchteanforderung</td>
                <td>Auswahl der Feuchteanforderung</td>
              </tr>
              <tr>
                <td>Wärmerückgewinnung</td>
                <td>Auswahl der Wärmerückgewinnung</td>
              </tr>
              <tr>
                <td>Nennkälteleistung</td>
                <td>Eingabe der Nennkälteleistung</td>
              </tr>
              <tr>
                <td>Elektrische Nennantriebsleistung</td>
                <td>Eingabe der elektrischen Nennantriebsleistung</td>
              </tr>
              <tr>
                <td>Jährlicher elektrischer Energiebedarf</td>
                <td>Eingabe des jährlichen elektrischen Energiebedarfs</td>
              </tr>
              <tr>
                <td>Jährliche Betriebsstunden</td>
                <td>Eingabe Jährliche Betriebsstunden in h</td>
              </tr>
              <tr>
                <td>Rückkühlung</td>
                <td>Auswahl der Rückkühlung</td>
              </tr>
              <tr>
                <td>Art des Verdichters und/oder der Teillastregelung</td>
                <td>Auswahl der Verdichterart und/oder der Teillastregelung</td>
              </tr>
              <tr>
                <td>Art der Rückkühlung</td>
                <td>Auswahl der Rückkühlung</td>
              </tr>
              <tr>
                <td>Art des Rückkühlers</td>
                <td>Auswahl des Rückkühlers</td>
              </tr>
            </tbody>
          </table>

          <table>
            <caption>Parameter der Sorptionskältemaschine</caption>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Nennkälteleistung</td>
                <td>Auswahl der Rücklauftemperatur</td>
              </tr>
              <tr>
                <td>Jährliche Betriebsstunden</td>
                <td>Eingabe Jährliche Betriebsstunden in h</td>
              </tr>
              <tr>
                <td>Jährlicher thermischer Energiebedarf</td>
                <td>Eingabe Jährlicher thermischer Energiebedarf</td>
              </tr>
              <tr>
                <td>Kältemittel/Lösungsmittel Paar</td>
                <td>Auswahl des Kältemittel/Lösungsmittel Paars</td>
              </tr>
              <tr>
                <td>Art des Verdichters und/oder der Teillastregelung</td>
                <td>Auswahl der Verdichterart und/oder der Teillastregelung</td>
              </tr>
            </tbody>
          </table>

          <table>
            <caption>Parameter der Raumklimasystem</caption>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>SEER bekannt</td>
                <td>Auswahl ob SEER bekannt</td>
              </tr>
              <tr>
                <td>Jahresarbeitszahl</td>
                <td>Eingabe der Jahresarbeitszahl im Kühlbetrieb</td>
              </tr>
              <tr>
                <td>Nutzungsart</td>
                <td>Auswahl der Anzahl an unterschiedlichen Nutzungsarten</td>
              </tr>
              <tr>
                <td>Nutzungsprofil</td>
                <td>Auswahl des Nutzungsprofiles im Gebäude</td>
              </tr>
              <tr>
                <td>Anteil der Nutzungsart</td>
                <td>Eingabe des prozentualen Nutzungsartanteils an der Gesamtfläche</td>
              </tr>
              <tr>
                <td>Betriebsart</td>
                <td>Raumkühlung</td>
              </tr>
              <tr>
                <td>Nennkälteleistung</td>
                <td>Eingabe der Nennkälteleistung</td>
              </tr>
              <tr>
                <td>Jährlicher elektrischer Energiebedarf</td>
                <td>Eingabe des jährlichen elektrischen Energiebedarfs</td>
              </tr>
              <tr>
                <td>Jährliche Betriebsstunden</td>
                <td>Eingabe Jährliche Betriebsstunden in h</td>
              </tr>
              <tr>
                <td>Anlagensystem</td>
                <td>Auswahl des Anlagensystems</td>
              </tr>
              <tr>
                <td>Art des Verdichters und/oder der Teillastregelung</td>
                <td>Auswahl der Verdichterart und/oder der Teillastregelung</td>
              </tr>
            </tbody>
          </table>

          <table>
            <caption>Finanzielle Parameter</caption>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Demontagekosten (Referenz)</td>
                <td>
                  Gesamtkosten der Demontagekosten inkl. Arbeitszeit, 
                  Maschinenkosten und Entsorgungskosten.
                </td>
              </tr>
              <tr>
                <td>Kosten Neuanlage (Geplant)</td>
                <td>Gesamte Anschaffungskosten bis hin zur Inbetriebnahme.</td>
              </tr>
              <tr>
                <td>Betriebs- und Instandhaltungskosten</td>
                <td>
                  Jährliche Kosten für Betriebsmaterial und
                  Instandhaltungsarbeiten (ohne Energiekosten).
                </td>
              </tr>
            </tbody>
          </table>

        </v-expansion-panel-text>
      </v-expansion-panel>

      
      <!-- ======================================== -->
      <!-- PANEL 3 — BERECHNUNGSGRUNDLAGE & DATEN -->
      <!-- ======================================== -->

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Berechnungsgrundlage und Daten</div>
        </v-expansion-panel-title>

        <v-expansion-panel-text eager>

          <h2>Endenergiebedarf für die Kälteerzeugung</h2>

          <h3>Kompressionskältemaschinen</h3>

          <p>
            Die energetische Bewertung von Kaltdampf-Kompressionskältemaschinen
            erfolgt anhand der Nennkälteleistungszahl \(EER\) und eines mittleren
            Teillastfaktors \(PLV_\mathrm{av}\). Auf Basis dieser Werte wird die
            Jahreskälteleistungszahl \(SEER\) wie folgt berechnet:
          </p>

          <p>
            $$ SEER = EER \cdot PLV_\mathrm{av} =
            \frac{Q_\mathrm{C,f}}{Q_\mathrm{C,outg}} $$
          </p>

          <p> Dabei sind </p>

          <ul>
            <li>\(EER\): die Nennkälteleistungszahl (energy efficiency ratio)</li>
            <li>\(PLV_\mathrm{av}\): der mittlere Teillastfaktor (part load value)</li>
            <li>\(SEER\): Jahreskälteleistungszahl</li>
            <li>\(Q_\mathrm{C,f}\): elektrischer Endenergiebedarf</li>
            <li>\(Q_\mathrm{C,outg}\): Erzeugerkälteabgabe</li>
          </ul>

          <p>
            Die Nennkälteleistungszahl EER stellt das Verhältnis aus
            Nennkälteleistung (Nutzen) und elektrischer Antriebsleistung (Aufwand)
            unter Auslegungsbedingungen dar:
          </p>

          <p>
            $$EER = \frac{\dot{Q}_\mathrm{C,outg}}{P_\mathrm{C,elektr}}$$
          </p>

          <p> Dabei sind: </p>
          <ul>
            <li>\(\dot{Q}_\mathrm{C,outg}\): Nennkälteleistung</li>
            <li>\(P_\mathrm{C,elektr}\): elektrische Nennantriebsleistung</li>
          </ul>

          <p>
            Die Leistungszahl variiert stark unter Teillast. Diese Bedingungen werden
            mit dem technologieabhängigen mittleren Teillastfaktor
            \(PLV_\mathrm{av}\) abgebildet, welcher das reale Lastverhalten,
            Außentemperaturen und Überdimensionierung berücksichtigt.
          </p>


          <!-- ===================== -->
          <!-- WASSERGEKÜHLTE MASCH. -->
          <!-- ===================== -->

          <h4>Wassergekühlte Kompressionskältemaschinen</h4>
          <p>Die DIN 18599‑7 unterscheidet folgende Verdichter-/Regelungsarten:</p>

          <table>
            <thead>
              <tr>
                <th></th>
                <th>Art des Verdichters & Teillastregelung</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>(1)</td><td>Kolben-/Scrollverdichter, Zweipunktregelung (EIN/AUS)</td></tr>
              <tr><td>(2)</td><td>Kolben-/Scroll, mehrstufig schaltbar</td></tr>
              <tr><td>(3)</td><td>Kolbenverdichter mit Zylinderabschaltung</td></tr>
              <tr><td>(4)</td><td>Heißgasbypassregelung</td></tr>
              <tr><td>(5)</td><td>Schraubenverdichter mit Steuerschieber</td></tr>
              <tr><td>(6)</td><td>Turboverdichter mit Einlassdrossel</td></tr>
              <tr><td>(7)</td><td>Invertergeregelte Schraubenverdichter</td></tr>
              <tr><td>(8)</td><td>Invertergeregelte Turboverdichter</td></tr>
            </tbody>
          </table>

          <p><b>Hinweise:</b></p>
          <ul>
            <li>PLV abhängig von Verdichter, Regelung, Nutzungsprofil (DIN 18599‑10)</li>
            <li>RLT-Systeme benötigen Luftbehandlung (Befeuchtung, WRG)</li>
            <li>Bei mehreren Nutzungsarten: gewichtete PLVs</li>
            <li>Unzulässige Kombinationen → DIN 18599‑7 Anhang A.3</li>
          </ul>


          <!-- ===================== -->
          <!-- LUFTGEKÜHLTE MASCH. -->
          <!-- ===================== -->

          <h4>Luftgekühlte Kompressionskältemaschinen</h4>

          <p>DIN 18599‑7 unterscheidet folgende Verdichterarten:</p>

          <table>
            <thead>
              <tr><th></th><th>Verdichter & Teillastregelung</th></tr>
            </thead>
            <tbody>
              <tr><td>(A)</td><td>Zweipunktregelung, taktend, Pufferspeicher</td></tr>
              <tr><td>(B)</td><td>Mehrstufig schaltbare Kolben-/Scrollverbunde</td></tr>
              <tr><td>(C)</td><td>Schraubenverdichter mit Steuerschieberregelung</td></tr>
              <tr><td>(D)</td><td>Invertergeregelte Schraubenverdichter</td></tr>
              <tr><td>(E)</td><td>Invertergeregelte Turboverdichter</td></tr>
              <tr><td>(F)</td><td>Digitaler Scrollverdichter</td></tr>
            </tbody>
          </table>

          <p><b>Hinweise:</b></p>
          <ul>
            <li>PLV abhängig von Nutzung, Systemart, Rückkühlerkonzept</li>
            <li>RLT-Luftbehandlung relevant</li>
            <li>Für viele Kombinationen existieren keine PLVs → DIN Anhang A.3</li>
          </ul>


          <!-- ===================== -->
          <!-- RAUMKLIMASYSTEME -->
          <!-- ===================== -->

          <h4>Raumklimasysteme</h4>

          <p>
            Raumklimageräte ≤ 12 kW unterliegen EU‑Energieetiketten.  
            SEER-Werte können direkt übernommen werden.  
            Für Altgeräte / >12 kW → wie luftgekühlte KKM rechnen.
          </p>

          <p>DIN unterscheidet diese Regelungsarten:</p>

          <table>
            <thead>
              <tr><th></th><th>Verdichter-/Regelungsart</th></tr>
            </thead>
            <tbody>
              <tr><td>(a)</td><td>Zweipunktregelung (EIN/AUS) Einzonensystem</td></tr>
              <tr><td>(b)</td><td>Zweipunktregelung Mehrzonensystem</td></tr>
              <tr><td>(c)</td><td>Stetig (Inverter/taktend) Einzone, Expansionsventil</td></tr>
              <tr><td>(d)</td><td>Stetig (Inverter/taktend) Mehrzone, Expansionsventil</td></tr>
            </tbody>
          </table>


          <!-- ===================== -->
          <!-- SORPTIONSKÄLTEMASCHINEN -->
          <!-- ===================== -->

          <h3>Ab‑/Adsorptionskältemaschinen</h3>

          <p>
            Die Bewertung erfolgt über Wärmeverhältnis \(\zeta\) und PLV:
          </p>

          <p>
            $$ \zeta_\mathrm{av} = \zeta \cdot PLV_\mathrm{av} =
            \frac{Q_\mathrm{C,out,g}}{Q_\mathrm{C,f}} $$
          </p>

          <p>Definitionen:</p>

          <ul>
            <li>\(\zeta\): Nennwärmeverhältnis</li>
            <li>\(PLV_\mathrm{av}\): mittlerer Teillastfaktor</li>
            <li>\(\zeta_\mathrm{av}\): mittleres Wärmeverhältnis</li>
            <li>\(Q_\mathrm{C,f}\): thermischer Endenergiebedarf</li>
          </ul>

          <p>Auslegungsgleichung:</p>
          <p>
            $$ \zeta = \frac{\dot{Q}_\mathrm{C,outg}}{\dot{Q}_\mathrm{C,therm}} $$
          </p>

          <ul>
            <li>\(\dot{Q}_\mathrm{C,outg}\): Nennkälteleistung</li>
            <li>\(\dot{Q}_\mathrm{C,therm}\): Nennheizwärmeleistung</li>
          </ul>

          <p>
            Zusätzlich wird elektrische Hilfsenergie für Pumpen benötigt:
          </p>

          <p>
            $$ W_\mathrm{C,f,therm} =
            \frac{\dot{Q}_\mathrm{C,outg} \cdot P_\mathrm{el,C,therm} \cdot t_\mathrm{op}}{1000} $$
          </p>

          <ul>
            <li>\(W_\mathrm{C,f,therm}\): Hilfsenergiebedarf</li>
            <li>\(P_\mathrm{el,C,therm}\): spezifischer Pumpenenergieaufwand</li>
            <li>\(t_\mathrm{op}\): Betriebszeit</li>
          </ul>

          <h4>Hinweise</h4>
          <ul>
            <li>Sorptionskältemaschinen sind immer wassergekühlt</li>
            <li>PLV unabhängig von Nutzung und Betriebsart</li>
            <li>Standard‑PLV = 0.95 mangels validierter Daten</li>
            <li>Nutzungsfaktor Rückkühler \(f_R = 0.65\)</li>
          </ul>


          <!-- ===================== -->
          <!-- RÜCKKÜHLUNG -->
          <!-- ===================== -->

          <h3>Endenergiebedarf für die Rückkühlung</h3>

          <p>
            Elektrischer Endenergiebedarf:
          </p>

          <p>
            $$W_\mathrm{C,f,R} = \dot{Q}_\mathrm{R,outg} \cdot
            q_\mathrm{R,elektr} \cdot f_\mathrm{R,av} \cdot t_\mathrm{R,op}
            \cdot f_\mathrm{R,FC}$$
          </p>

          <p>Definitionen:</p>

          <ul>
            <li>\(\dot{Q}_\mathrm{R,outg}\): Nennrückkühlleistung</li>
            <li>\(q_\mathrm{R,elektr}\): Elektroenergieaufwand Rückkühler</li>
            <li>\(f_\mathrm{R,av}\): mittlerer Nutzungsfaktor</li>
            <li>\(t_\mathrm{R,op}\): Betriebszeit Rückkühler</li>
            <li>\(f_\mathrm{R,FC}\): Anteil freie Kühlung</li>
          </ul>

          <p>Abhängig vom Maschinentyp:</p>

          <p>
            $$\dot{Q}_\mathrm{R,outg} =
            \dot{Q}_\mathrm{C,outg} \cdot \left(1 + \frac{1}{EER}\right)
            \quad \text{(Kompressionskälte)} $$
          </p>

          <p>
            $$\dot{Q}_\mathrm{R,outg} =
            \dot{Q}_\mathrm{C,outg} \cdot \left(1 + \frac{1}{\zeta}\right)
            \quad \text{(Sorptionskälte)} $$
          </p>

          <p><b>Hinweise:</b></p>
          <ul>
            <li>Abhängig von Rückkühlerbauweise & Nutzungsprofil</li>
            <li>Luftgekühlte KKM: Rückkühlenergie bereits enthalten</li>
          </ul>

        </v-expansion-panel-text>
      </v-expansion-panel>

      
      <!-- ======================================== -->
      <!-- PANEL 4 — ZUSÄTZLICHE INFORMATIONEN -->
      <!-- ======================================== -->

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Zusätzliche Informationen</div>
        </v-expansion-panel-title>

        <v-expansion-panel-text eager>
          <v-container>
            <p>
              Das Verfahren nach DIN 18599-7 ermöglicht die Berechnung der elektrischen
              Endenergie von Kompressionskältemaschinen (KKM) und der thermischen
              Endenergie von Sorptionskältemaschinen (AKM) einschließlich der
              elektrischen Endenergie für die Rückkühlung. Die im folgenden Bild
              dargestellten Kälteerzeugungssysteme sind mittels Kennwertverfahren
              abbildbar.
            </p>

            <img
              :src="chillersvg"
              alt="Kältemäschine"
            >
            <ul style="list-style-type:none;">
              <li>A.1: Verdichter (I) → Kompressionskältemaschinen</li>
              <li>
                A.2: Absorber (J), Lösungsmittelpumpe (L), Desorber (L) und
                Expansionsventil → Sorptionskältemaschinen
              </li>
            </ul>
            <b>B: Kondensator</b> mit
            <ul style="list-style-type:none;">
              <li>B.1: luftgekühlter Kondensator → luftgekühlte Kältemaschinen</li>
              <li>
                B.2: wassergekühlter Kondensator mit Rückkühler (E) und Kühlwasserpumpe
                (F) → wassergekühlte Kältemaschinen
              </li>
            </ul>
            <b>C: Expansionsventil</b>
            <b>D: Verdampfer</b> mit
            <ul style="list-style-type:none;">
              <li>D.1: direkt im Luftkreis → Direktverdampfer</li>
              <li>
                D.2: im Kaltwasserkreis mit Luftkühler (G) und Kaltwasserpumpe (H)
              </li>
            </ul>
            <b>E: Rückkühler</b> mit
            <ul style="list-style-type:none;">
              <li>E.1: trockener Wärmeabfuhr → Trockenrückkühler</li>
              <li>E.2: nasser Wärmeabfuhr → Verdunstungskühler</li>
            </ul>

            <p>
              Allgemein berechnet sich der Endenergiebedarf für die Kälteerzeugung aus
              dem Energiebedarf der Kältemaschine und dem Energiebedarf für die
              Rückkühlung. Dabei ist die Endenergie der Kältemaschine in elektrische
              Endenergie (Strom) bei Kompressionskälteanlagen und thermische Endenergie
              (Heißwasser) bei Sorptionskälteanlagen zu unterscheiden. Die Endenergie
              der Rückkühlung ist elektrische Endenergie (Strom).
            </p>

            <h3>Erzeugernutzkälteabgabe</h3>
            <p>
              Die von einer Kältemaschine versorgten Gebäudezone werden als
              Kälteversorgungsbereiche bezeichnet. Innerhalb eines solchen
              Kälteversorgungsbereich können mehreren <b>Nutzungsarten</b> mit
              unterschiedlichen <b>Nutzungsprofilen</b> nach DIN V 18599-10 vorliegen.
              Die Nutzkälte kann innerhalb einer Gebäudezone über ein
              <b>RLT-System</b> oder ein <b>Raumsystem</b> bereitgestellt werden.
            </p>
            <p>
              Die Erzeugernutzkälteabgabe einer Kältemaschine wird je Nutzungsart
              getrennt ermittelt. Die Gesamtsumme der jährlichen Erzeugernutzkälteabgabe
              der Kältemaschine eines Kälteversorgungsbereiches ergibt sich aus der
              Summe der Erzeugernutzkälteabgabe aller Nutzungsarten.
            </p>

            <h4>Hinweise</h4>
            <ul>
              <li>
                In der aktuellen Version von EARNS ist es lediglich möglich, eine
                Gebäudezone mit maximal drei unterschiedlichen Nutzungsarten zu
                definieren.
              </li>
              <li>
                Die Berechnungsvorschrift der DIN 18599-7 fordert nicht für alle
                Kältemaschinentypen eine Definition der Nutzungsarten.
              </li>
            </ul>

            <hr />

            <hr />

            <h2>Freie Kühlung</h2>
            <p>
              Der nutzbare Anteil der freien Kühlung mittels Anlagentechnik ist abhängig
              von:
            </p>
            <ul>
              <li>
                Nutzungsart und Bauschwere der gekühlten Zone
              </li>
              <li>
                Bauart der Rückkühlung (Trocken-/Verdunstungskühler)
              </li>
              <li>
                Temperaturniveau des Kaltwasserkreislaufs
              </li>
              <li>
                Betriebsweise (Parallelbetrieb, Alternativbetrieb) und Regelung der
                Rückkühlung
              </li>
              <li>
                Auslegungsfaktor für die Rückkühler
              </li>
            </ul>

            <h3>Freie Kühlung mittels Rückkühlwerken</h3>
            <p>
              Bei der Freien Kühlung mittels Rückkühlwerken werden vorhandene
              Rückkühlwerke in Zeiten reduzierter thermischer Belastungen und damit
              geringerer Kühllasten direkt zur Kälteversorgung genutzt. Die
              Kältemaschine wird dabei nicht in Betrieb genommen
              (<b>Alternativbetrieb</b>) oder kann bei mehreren Rückkühlwerken gemeinsam
              mit der Freien Rückkühlung genutzt werden (<b>Parallelbetrieb</b>).
            </p>

            <h3>
              Freie Kühlung mittels luftgekühlten Kältemaschinen mit integrierten
              Freikühlregistern
            </h3>
            <p>
              Die maschinenintegrierte Freie Kühlung von luftgekühlten Kältemaschinen
              wird von den Herstellern mit verschiedenen Verfahren realisiert. Am
              häufigsten werden so genannte Freikühlregister im Parallelbetrieb mit den
              Kondensatoren betrieben, die kaltwasserdurchströmt und rücklaufseitig
              angeordnet sind. Als Betriebsweise ist daher nur
              <b>Parallelbetrieb</b> möglich. Des Weiteren können nur folgenden
              Teillastregelarten ausgewählt werden:
            </p>
            <ul>
              <li>
                (B): Kolben-/Scrollverdichter mehrstufig schaltbar (mindestens vier
                Schaltstufen als Verdichterverbund)
              </li>
              <li>
                (C): Schraubenverdichter mit Steuerschieberregelung
              </li>
              <li>
                (D): Invertergeregelter Schraubenverdichter
              </li>
            </ul>

            <hr />

            <h2>Geothermische Kühlung</h2>
            <p>
              work in progress...
            </p>
          </v-container>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- ======================================== -->
      <!-- PANEL 5 — QUELLEN -->
      <!-- ======================================== -->

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Quellen</div>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <h2>Quellen</h2>
          <ul>
            <li>
              DIN V 18599-7: Energetische Bewertung von Gebäuden - Berechnung des
              Nutz-, End- und Primärenergiebedarfs für Heizung, Kühlung, Lüftung,
              Trinkwarmwasser und Beleuchtung - Teil 7: Endenergiebedarf von
              Raumlufttechnik- und Klimakältesystemen für den Nichtwohnungsbau
            </li>
            <li>
              Schiller, Heiko; Mai, Roony: Berechnungs-Algorithmen für Freie und
              Regenerative Kühltechnologien in Nichtwohngebäuden. Fraunhofer IRB
              Verlag
            </li>
          </ul>
        </v-expansion-panel-text>
      </v-expansion-panel>

    </v-expansion-panels>

  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import chillersvg from '@/assets/help/chiller/chiller.svg'

onMounted(() => {
  if (typeof window !== 'undefined' && window.MathJax && typeof window.MathJax.typeset === 'function') {
    window.MathJax.typeset()
  }
})
</script>

<style scoped>
/* optional: keep typography tweaks consistent with BoilerHelp.vue */
</style>
